import { useState } from 'react';

import { selectAppConfig } from '@vlabs/shared/selectors/appSelectors';
import { useSelector } from 'react-redux';

import type {
  TClassificationResult,
  TClassifyDeepfakeStatus,
  TFrame,
} from './types';
import { validateFrames } from './validateFrames';
import {
  classifyDeepfake,
  pollClassifyDeepfakeTask,
} from '../api/classifyDeepfake';

interface UseClassifyDeepfakeParams {
  frames: TFrame[];
  enabled?: boolean;
  ttl?: number;
}

interface UseClassifyDeepfakeReturn {
  status: TClassifyDeepfakeStatus;
  result: TClassificationResult | null;
  error: unknown;
  canClassify: boolean;
  runClassification: () => Promise<void>;
}

export const useClassifyDeepfake = ({
  frames,
  enabled = true,
  ttl = 7,
}: UseClassifyDeepfakeParams): UseClassifyDeepfakeReturn => {
  const [status, setStatus] = useState<TClassifyDeepfakeStatus>('idle');
  const [result, setResult] = useState<TClassificationResult | null>(null);
  const [error, setError] = useState<unknown>(null);

  const appConfig = useSelector(selectAppConfig);
  const servicePath = appConfig?.deepfakeClassifier?.servicePath;

  const canClassify = enabled && !!servicePath && validateFrames(frames);

  const runClassification = async (): Promise<void> => {
    if (!canClassify && !servicePath) return;

    try {
      setStatus('loading');

      let deferredTask;

      if (ttl) {
        deferredTask = {
          enable: 1 as const,
          storage_policy: {
            storage_type: 'luna_image_store',
            ttl,
          },
        };
      } else {
        deferredTask = {
          enable: 0 as const,
        };
      }

      const payload = {
        frames,
        deferred_task: deferredTask,
      };

      const response = await classifyDeepfake(servicePath, payload);

      if (response.status === 'done') {
        setResult({
          type: response.result.attack_class,
          instrument: response.result.generation_algorithm,
          score: response.result.score,
        });
        setStatus('success');
        return;
      }

      if (response.status === 'queued') {
        const taskResult = await pollClassifyDeepfakeTask({
          servicePath,
          taskId: response.task_id,
        });

        setResult({
          type: taskResult.attack_class,
          instrument: taskResult.generation_algorithm,
          score: taskResult.score,
        });
        setStatus('success');
        return;
      }

      throw new Error('Unknown deepfake classifier response status');
    } catch (err) {
      setError(err);
    }
  };

  return {
    status,
    result,
    error,
    canClassify,
    runClassification,
  };
};
